import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, App, Divider, Typography, Switch } from 'antd';
import { UserOutlined, LockOutlined, SaveOutlined, ArrowLeftOutlined, BulbOutlined, MoonOutlined, SunOutlined } from '@ant-design/icons';
import { CitadeloLayout } from '../../components/CitadeloLayout';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import { apiService } from '../../services/api';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

export const MyProfile: React.FC = () => {
  const { user, logout, login } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        username: user.username,
        email: user.email,
        name: (user as any).name || '',
        surname: (user as any).surname || '',
      });
    }
  }, [user, form]);

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const updatePayload: any = {
        name: values.name,
        surname: values.surname,
      };

      if (values.newPassword) {
        if (values.newPassword !== values.confirmPassword) {
          message.error('New passwords do not match');
          setLoading(false);
          return;
        }

        if (!values.oldPassword) {
          message.error('Please enter your old password to set a new one');
          setLoading(false);
          return;
        }

        // Verify old password by attempting to login
        const loginResult = await login(user?.username || '', values.oldPassword);
        
        if (!loginResult.success) {
           message.error('Incorrect old password');
           setLoading(false);
           return;
        }

        updatePayload.password = values.newPassword;
      }

      await apiService.updateUser(updatePayload);
      message.success('Profile updated successfully');
      
      // Clear password fields
      form.setFieldsValue({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      
    } catch (error) {
      console.error(error);
      message.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <CitadeloLayout onLogout={logout} showHeader={true}>
      <Card 
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Title level={3} style={{ margin: 0 }}>My Profile</Title>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/')}>
              Back to Projects
            </Button>
          </div>
        } 
        style={{ maxWidth: 800, margin: '0 auto' }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
        >
          <Title level={5}><UserOutlined /> Personal Information</Title>
          <Divider style={{ margin: '12px 0 24px 0' }} />
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item
              name="username"
              label="Username"
            >
              <Input disabled />
            </Form.Item>
            
            <Form.Item
              name="email"
              label="Email"
            >
              <Input disabled />
            </Form.Item>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item
              name="name"
              label="First Name"
              rules={[{ required: true, message: 'Please input your first name!' }]}
            >
              <Input placeholder="John" />
            </Form.Item>

            <Form.Item
              name="surname"
              label="Last Name"
              rules={[{ required: true, message: 'Please input your last name!' }]}
            >
              <Input placeholder="Doe" />
            </Form.Item>
          </div>

          <Title level={5} style={{ marginTop: 24 }}><BulbOutlined /> Appearance</Title>
          <Divider style={{ margin: '12px 0 24px 0' }} />
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            padding: '16px',
            background: isDarkMode ? '#262626' : '#f5f5f5',
            borderRadius: '8px',
            marginBottom: '24px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {isDarkMode ? <MoonOutlined style={{ fontSize: 20 }} /> : <SunOutlined style={{ fontSize: 20 }} />}
              <div>
                <Text strong style={{ display: 'block' }}>Dark Mode</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {isDarkMode ? 'Switch to light theme' : 'Switch to dark theme'}
                </Text>
              </div>
            </div>
            <Switch 
              checked={isDarkMode} 
              onChange={toggleTheme}
              checkedChildren={<MoonOutlined />}
              unCheckedChildren={<SunOutlined />}
            />
          </div>

          <Title level={5} style={{ marginTop: 24 }}><LockOutlined /> Security</Title>
          <Divider style={{ margin: '12px 0 24px 0' }} />
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Leave blank if you don't want to change your password.
          </Text>

          <Form.Item
            name="oldPassword"
            label="Current Password"
          >
            <Input.Password placeholder="Required to set new password" />
          </Form.Item>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item
              name="newPassword"
              label="New Password"
              rules={[{ min: 6, message: 'Password must be at least 6 characters' }]}
            >
              <Input.Password placeholder="New password" />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="Confirm New Password"
              dependencies={['newPassword']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('The two passwords that you entered do not match!'));
                  },
                }),
              ]}
            >
              <Input.Password placeholder="Confirm new password" />
            </Form.Item>
          </div>

          <Form.Item style={{ marginTop: 24 }}>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading} block size="large">
              Save Changes
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </CitadeloLayout>
  );
};

